import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { PanelShell, PlnBarra, PlnBarraRot, PlnBarraSep, PlnBloco, stickyTh } from './PanelShell'
import { LuBarcode } from 'react-icons/lu'
import {
  FiLoader, FiSearch, FiX, FiRefreshCw, FiExternalLink, FiSlash, FiTrash2, FiFileText,
  FiChevronDown, FiChevronRight,
} from 'react-icons/fi'

// Boletos gerados (2026-07-20) — histórico de TODAS as cobranças Asaas
// (tabela local asaas_payment, alimentada pelo sync de 15 min + botão manual).
// Ações: abrir/baixar PDF ("reenviar" = baixar e mandar por onde quiser,
// decisão do dono), cancelar (Asaas, admin) e excluir (só cancelado, admin).

interface Boleto {
  payment_id: string
  installment_id: string | null
  customer_name: string | null
  customer_doc: string | null
  valor: number | string
  net_value: number | string | null
  due_date: string
  payment_date: string | null
  status: string
  billing_type: string | null
  description: string | null
  created_at: string
  // Boleto gerado pelo cob.twms (03/11/2026): a parte da ADVOGADA nesta parcela
  // (juros + multa + honorários, proporcional) — o cob calcula, o fin só mostra.
  cob?: { advogada: number; diretor: number; criado_por: string; parcelas: number; total_negociado: number; advogada_total: number; modo: string }
}

const brl = (n: number | string) => Number(n).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
// DATE do mysql2 chega serializado ISO ('2026-07-01T03:00:00.000Z' ou 'YYYY-MM-DD')
const ptDate = (s: string | null) => s ? String(s).slice(0, 10).split('-').reverse().join('/') : null

const STATUS_META: Record<string, { label: string; cls: string }> = {
  PENDING: { label: 'Em aberto', cls: 'neu-tag bg-sky-500/15 text-sky-600 dark:text-sky-400' },
  OVERDUE: { label: 'Vencido', cls: 'neu-tag bg-red-500/15 text-red-600 dark:text-red-400' },
  RECEIVED: { label: 'Pago', cls: 'neu-tag bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  CONFIRMED: { label: 'Pago', cls: 'neu-tag bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  RECEIVED_IN_CASH: { label: 'Pago', cls: 'neu-tag bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  DELETED: { label: 'Cancelado', cls: 'neu-tag bg-red-500/20 text-red-600 dark:text-red-400 font-semibold' },
  REFUNDED: { label: 'Estornado', cls: 'neu-tag bg-violet-500/15 text-violet-600 dark:text-violet-400' },
  REFUND_REQUESTED: { label: 'Estornando', cls: 'neu-tag bg-violet-500/15 text-violet-600 dark:text-violet-400' },
  REFUND_IN_PROGRESS: { label: 'Estornando', cls: 'neu-tag bg-violet-500/15 text-violet-600 dark:text-violet-400' },
}

const CHIPS: Array<{ k: string; label: string }> = [
  { k: '', label: 'Todos' },
  { k: 'pendente', label: 'Pendentes' },
  { k: 'pago', label: 'Pagos' },
  { k: 'vencido', label: 'Vencidos' },
  { k: 'cancelado', label: 'Cancelados' },
]

export function BoletosGerados() {
  const { authenticatedFetch, user } = useAuth()
  const [rows, setRows] = useState<Boleto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [status, setStatus] = useState('')
  const [syncing, setSyncing] = useState(false)
  const [busy, setBusy] = useState<Record<string, boolean>>({})

  // seq guarda contra resposta fora de ordem (troca de filtro com request em voo)
  const loadSeqRef = useRef(0)
  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (from) params.set('from', from)
      if (to) params.set('to', to)
      if (status) params.set('status', status)
      const r = await authenticatedFetch(`/api/asaas/boletos?${params}`)
      const d = await r.json()
      if (seq !== loadSeqRef.current) return // filtros mudaram no meio — descarta
      if (d.success) setRows(d.boletos)
      else setError(d.error || 'Falha ao carregar')
    } catch { if (seq === loadSeqRef.current) setError('Erro de conexão') }
    finally { if (seq === loadSeqRef.current) setLoading(false) }
  }, [authenticatedFetch, q, from, to, status])

  // Busca com debounce (o resto dos filtros recarrega direto pelo load memoizado)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(load, 300)
    return () => clearTimeout(debounceRef.current)
  }, [load])

  const sync = async () => {
    if (syncing) return
    setSyncing(true)
    try {
      const r = await authenticatedFetch('/api/asaas/sync', { method: 'POST' })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'Falha no sync')
      await load()
    } catch (e) { setError(e instanceof Error ? e.message : 'Erro no sync') }
    finally { setSyncing(false) }
  }

  // "Reenviar" = abrir/baixar o PDF pra mandar por onde quiser (WhatsApp,
  // e-mail…). O PDF vem do proxy autenticado do crm — abre numa aba.
  const abrirPdf = async (b: Boleto, carne = false) => {
    const key = `pdf-${b.payment_id}${carne ? '-c' : ''}`
    setBusy(s => ({ ...s, [key]: true }))
    try {
      const path = carne && b.installment_id
        ? `/api/cobranca/asaas/parcelamento/${b.installment_id}/pdf`
        : `/api/cobranca/asaas/boleto/${b.payment_id}/pdf`
      const r = await authenticatedFetch(path)
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `Erro ${r.status}`) }
      const url = URL.createObjectURL(await r.blob())
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e) { setError(e instanceof Error ? e.message : 'Erro ao abrir o PDF') }
    finally { setBusy(s => ({ ...s, [key]: false })) }
  }

  const cancelar = async (b: Boleto) => {
    if (!window.confirm(`Cancelar o boleto de ${brl(b.valor)} de ${b.customer_name || b.customer_doc || 'cliente'} (venc. ${ptDate(b.due_date)})?\n\nA cobrança deixa de valer no Asaas e a linha fica vermelha como Cancelado.`)) return
    setBusy(s => ({ ...s, [`c-${b.payment_id}`]: true }))
    try {
      const r = await authenticatedFetch(`/api/asaas/boletos/${b.payment_id}/cancel`, { method: 'POST' })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'Falha ao cancelar')
      await load()
    } catch (e) { setError(e instanceof Error ? e.message : 'Erro ao cancelar') }
    finally { setBusy(s => ({ ...s, [`c-${b.payment_id}`]: false })) }
  }

  // ── Agrupamento por pessoa (pedido do dono 2026-07-20): uma linha por
  // cliente com totais; clique expande os boletos. Chave = CPF (fallback nome).
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggleGroup = (key: string) => setExpanded(prev => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })
  interface Grupo { key: string; nome: string; doc: string | null; itens: Boleto[]; aberto: number; pago: number; nAberto: number; nPago: number; nCancelado: number; nVencido: number; advPago: number; advAberto: number }
  const grupos = useMemo<Grupo[]>(() => {
    const map = new Map<string, Grupo>()
    for (const b of rows) {
      const key = b.customer_doc || b.customer_name || b.payment_id
      let g = map.get(key)
      if (!g) { g = { key, nome: b.customer_name || b.customer_doc || '—', doc: b.customer_doc, itens: [], aberto: 0, pago: 0, nAberto: 0, nPago: 0, nCancelado: 0, nVencido: 0, advPago: 0, advAberto: 0 }; map.set(key, g) }
      g.itens.push(b)
      const v = Number(b.valor)
      const adv = b.cob ? b.cob.advogada : 0
      if (['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(b.status)) { g.pago += v; g.nPago++; g.advPago += adv }
      else if (b.status === 'DELETED') g.nCancelado++
      else if (b.status === 'OVERDUE') { g.aberto += v; g.nVencido++; g.advAberto += adv }
      else if (!b.status.startsWith('REFUND')) { g.aberto += v; g.nAberto++; g.advAberto += adv }
      else { g.aberto += v; g.nAberto++ }
    }
    return [...map.values()] // ordem = 1ª aparição (rows já vêm por vencimento desc)
  }, [rows])
  // Repasse à advogada (boletos do cob): o que já entrou e o que ainda vai entrar.
  const advogada = useMemo(() => grupos.reduce((t, g) => ({ pago: t.pago + g.advPago, aberto: t.aberto + g.advAberto }), { pago: 0, aberto: 0 }), [grupos])
  const temCob = rows.some(b => b.cob)
  const buscando = q.trim().length > 0 // busca ativa expande tudo (poucos grupos)

  const excluir = async (b: Boleto) => {
    if (!window.confirm(`Excluir de vez o boleto cancelado de ${brl(b.valor)} de ${b.customer_name || 'cliente'}?\n\nSai do painel e não volta (no Asaas já está cancelado).`)) return
    setBusy(s => ({ ...s, [`x-${b.payment_id}`]: true }))
    try {
      const r = await authenticatedFetch(`/api/asaas/boletos/${b.payment_id}`, { method: 'DELETE' })
      const d = await r.json()
      if (!r.ok || !d.success) throw new Error(d.error || 'Falha ao excluir')
      await load()
    } catch (e) { setError(e instanceof Error ? e.message : 'Erro ao excluir') }
    finally { setBusy(s => ({ ...s, [`x-${b.payment_id}`]: false })) }
  }

  return (
    <PanelShell
      titulo="Boletos gerados"
      sub="Todas as cobranças emitidas no Asaas, agrupadas por cliente"
      icone={<LuBarcode />}
      acoes={
          <button onClick={sync} disabled={syncing}
            title="Buscar agora no Asaas o que acabou de ser emitido/pago (o sistema também sincroniza sozinho a cada 15 min)"
            className="neu-convex inline-flex items-center gap-1.5 h-9 rounded-lg border border-border bg-card text-foreground text-xs font-medium px-2.5 hover:bg-secondary transition-colors disabled:opacity-50">
            {syncing ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiRefreshCw className="w-3.5 h-3.5" />}
            Sincronizar Asaas
          </button>
      }>
      {/* Toolbar: busca + período (vencimento) + status.
          PLANOS (21/10/2026): tudo numa PlnBarra; o Sincronizar subiu pro cabeçalho
          e os chips de status entraram na mesma barra. */}
      <PlnBarra>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nome, CPF ou descrição…"
            title="Filtra por nome do cliente, CPF/CNPJ, descrição ou id da cobrança"
            className="neu-convex w-full rounded-lg border border-border bg-background pl-9 pr-7 py-2 text-sm text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
          {q && (
            <button onClick={() => setQ('')} title="Limpar busca"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <FiX className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <PlnBarraSep />
        <PlnBarraRot>Vencimento</PlnBarraRot>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)}
            title="Vencimento a partir de"
            className="neu-convex h-9 rounded-lg border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
          <span>até</span>
          <input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)}
            title="Vencimento até"
            className="neu-convex h-9 rounded-lg border border-border bg-background px-2 text-xs text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" />
          {(from || to) && (
            <button onClick={() => { setFrom(''); setTo('') }} title="Limpar período"
              className="text-muted-foreground hover:text-foreground"><FiX className="w-3.5 h-3.5" /></button>
          )}
        </div>
        <PlnBarraSep />
        <PlnBarraRot>Situação</PlnBarraRot>
        {/* Chips de status */}
        {CHIPS.map(c => (
          <button key={c.k || 'all'} onClick={() => setStatus(c.k)}
            className={`text-sm rounded-lg px-3 py-1.5 border transition-colors ${status === c.k ? 'neu-latch border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:text-foreground'}`}>
            {c.label}
          </button>
        ))}
      </PlnBarra>

      {error && (
        <div className="shrink-0 text-sm text-red-500 flex items-center gap-2">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-muted-foreground hover:text-foreground"><FiX className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {/* Tabela — única região com scroll; cabeçalho sticky (padrão PanelShell).
          PLANOS: a contagem pessoa(s)/boleto(s) virou a nota do bloco. */}
      <PlnBloco
        titulo="Cobranças por cliente"
        nota={loading ? undefined : `${grupos.length} pessoa(s) · ${rows.length} boleto(s) · clique no nome para ver os boletos${temCob ? ` · advogada (boletos do cob): ${brl(advogada.pago)} a repassar do que já foi pago, ${brl(advogada.aberto)} ainda em aberto` : ''}`}
        className="flex-1 min-h-[280px]"
      >
      <div className="glass-panel neu-table flex-1 min-h-0 overflow-auto scrollbar-thin">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="text-left text-xs text-muted-foreground">
              <th className={`px-4 py-2.5 font-medium w-28 ${stickyTh}`}>Vencimento</th>
              <th className={`px-4 py-2.5 font-medium ${stickyTh}`}>Cliente</th>
              <th className={`px-4 py-2.5 font-medium hidden xl:table-cell ${stickyTh}`}>Descrição</th>
              <th className={`px-4 py-2.5 font-medium w-32 text-right ${stickyTh}`}>Valor</th>
              <th className={`px-4 py-2.5 font-medium w-28 ${stickyTh}`}>Pagamento</th>
              <th className={`px-4 py-2.5 font-medium w-28 ${stickyTh}`}>Status</th>
              <th className={`px-4 py-2.5 font-medium w-36 text-right ${stickyTh}`}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground"><FiLoader className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                Nenhum boleto {q || from || to || status ? 'com esses filtros' : 'gerado ainda'}.
              </td></tr>
            ) : grupos.map(g => {
              const isOpen = expanded.has(g.key) || buscando
              return (
              <Fragment key={g.key}>
                {/* Linha-cabeçalho da pessoa: totais + expande/recolhe */}
                <tr onClick={() => toggleGroup(g.key)}
                  title={isOpen ? 'Recolher os boletos desta pessoa' : 'Expandir os boletos desta pessoa'}
                  className="border-b border-border/60 bg-secondary/20 hover:bg-secondary/40 cursor-pointer select-none">
                  <td colSpan={7} className="px-4 py-2.5">
                    <div className="flex items-center gap-3 flex-wrap">
                      {isOpen ? <FiChevronDown className="w-4 h-4 text-primary shrink-0" /> : <FiChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                      <span className="font-medium text-foreground truncate max-w-[320px]">{g.nome}</span>
                      {g.doc && <span className="text-[11px] text-muted-foreground font-mono">{g.doc}</span>}
                      <span className="text-xs text-muted-foreground">{g.itens.length} boleto{g.itens.length === 1 ? '' : 's'}</span>
                      <span className="flex-1" />
                      {g.nVencido > 0 && <span className="text-xs font-medium text-red-600 dark:text-red-400">{g.nVencido} vencido{g.nVencido === 1 ? '' : 's'}</span>}
                      {g.nCancelado > 0 && <span className="text-xs text-muted-foreground">{g.nCancelado} cancelado{g.nCancelado === 1 ? '' : 's'}</span>}
                      {g.aberto > 0 && <span className="text-xs tabular-nums text-sky-600 dark:text-sky-400" title="Soma dos boletos em aberto/vencidos">Em aberto <span className="font-semibold">{brl(g.aberto)}</span></span>}
                      {g.pago > 0 && <span className="text-xs tabular-nums text-emerald-600 dark:text-emerald-400" title="Soma dos boletos pagos">Pago <span className="font-semibold">{brl(g.pago)}</span></span>}
                      {g.advPago > 0 && <span className="text-xs tabular-nums text-amber-600 dark:text-amber-400" title="Boletos do cob já pagos: parte da advogada (juros + multa + honorários) que o diretor repassa">Advogada <span className="font-semibold">{brl(g.advPago)}</span></span>}
                    </div>
                  </td>
                </tr>
                {isOpen && g.itens.map(b => {
              const st = STATUS_META[b.status] || { label: b.status, cls: 'bg-secondary text-muted-foreground' }
              const cancelado = b.status === 'DELETED'
              const pago = ['RECEIVED', 'CONFIRMED', 'RECEIVED_IN_CASH'].includes(b.status)
              const estornado = b.status.startsWith('REFUND')
              return (
                <tr key={b.payment_id} className={`neu-tr border-b border-border/60 hover:bg-secondary/40 ${cancelado ? 'opacity-70' : ''}`}>
                  <td className="px-4 py-2.5 pl-8 text-foreground whitespace-nowrap tabular-nums">{ptDate(b.due_date)}</td>
                  <td className="px-4 py-2.5" />
                  <td className="px-4 py-2.5 hidden xl:table-cell">
                    <div className="text-muted-foreground truncate max-w-[320px]" title={b.description || undefined}>{b.description || '—'}</div>
                    {b.installment_id && <span className="text-[10px] font-medium text-violet-600 dark:text-violet-400">carnê</span>}
                    {b.cob && (
                      <div className="text-[11px] text-amber-700 dark:text-amber-400" title={`Gerado no cob por ${b.cob.criado_por} (modo ${b.cob.modo}). Negociado: ${brl(b.cob.total_negociado)}, dos quais ${brl(b.cob.advogada_total)} são da advogada.`}>
                        <span className="neu-tag rounded px-1 py-px bg-amber-500/15 font-semibold mr-1">cob</span>
                        advogada {brl(b.cob.advogada)} · diretor {brl(b.cob.diretor)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium tabular-nums text-foreground whitespace-nowrap">
                    {brl(b.valor)}
                    {b.cob && <div className="xl:hidden text-[10px] font-normal text-amber-700 dark:text-amber-400">cob · advogada {brl(b.cob.advogada)}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground whitespace-nowrap tabular-nums">{ptDate(b.payment_date) || '—'}</td>
                  <td className="px-4 py-2.5"><span className={`text-xs font-medium rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span></td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {!cancelado && (
                        <button onClick={() => abrirPdf(b)} disabled={busy[`pdf-${b.payment_id}`]}
                          title="Abrir/baixar o PDF do boleto (reenviar por onde quiser)"
                          className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-50">
                          {busy[`pdf-${b.payment_id}`] ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiExternalLink className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      {!cancelado && b.installment_id && (
                        <button onClick={() => abrirPdf(b, true)} disabled={busy[`pdf-${b.payment_id}-c`]}
                          title="Abrir o carnê completo (todas as parcelas num PDF)"
                          className="w-7 h-7 flex items-center justify-center rounded-md text-violet-500 hover:text-violet-600 hover:bg-violet-500/10 disabled:opacity-50">
                          {busy[`pdf-${b.payment_id}-c`] ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiFileText className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      {user?.isAdmin && !cancelado && !pago && !estornado && (
                        <button onClick={() => cancelar(b)} disabled={busy[`c-${b.payment_id}`]}
                          title="Cancelar no Asaas — a cobrança deixa de valer e a linha fica vermelha"
                          className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 disabled:opacity-50">
                          {busy[`c-${b.payment_id}`] ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSlash className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      {user?.isAdmin && cancelado && (
                        <button onClick={() => excluir(b)} disabled={busy[`x-${b.payment_id}`]}
                          title="Excluir de vez este boleto cancelado (sai do painel; no Asaas já está cancelado)"
                          className="w-7 h-7 flex items-center justify-center rounded-md text-red-500 hover:text-red-600 hover:bg-red-500/10 disabled:opacity-50">
                          {busy[`x-${b.payment_id}`] ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiTrash2 className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
                })}
              </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
      </PlnBloco>
    </PanelShell>
  )
}
